import { createRef, useState } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import UploadIcon from "@mui/icons-material/Upload";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import type { UploadFileMutationVariablesType } from "@/shared/types/common";

interface FileData {
  id: string;
  url: string;
}

interface FileUploadResponse {
  file: FileData;
}

interface FileQueryResponse {
  file: FileData;
}

interface FileDropzoneProps {
  accept?: string;
  deleteFileText?: string;
  fileUploadedText?: string;
  fileDeletedText?: string;
  downloadText?: string;
  onChange: (fileId: string | null) => void;
  label: string;
  helperText?: string;
  value?: string | null;
  required?: boolean;
  query: DocumentNode;
  mutation: DocumentNode;
  maxSize?: number;
}

export function FileDropzone({
  accept = "*",
  deleteFileText,
  fileUploadedText,
  fileDeletedText,
  downloadText,
  onChange,
  label,
  helperText,
  value,
  required,
  query,
  mutation,
  maxSize,
}: FileDropzoneProps) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const finalDeleteFileText = deleteFileText || "Удалить файл";
  const finalFileUploadedText = fileUploadedText || "Файл загружен";
  const finalFileDeletedText = fileDeletedText || "Файл удален";
  const finalDownloadText = downloadText || "Скачать файл";

  const [drag, setDrag] = useState<boolean>(false);

  const [mutate, mutationResult] = useMutation<
    FileUploadResponse,
    UploadFileMutationVariablesType
  >(mutation, {
    onCompleted({ file }) {
      onChange(file.id);
      enqueueSnackbar(finalFileUploadedText, { variant: "success" });
    },
    onError(error) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const queryResult = useQuery<FileQueryResponse, { id: string }>(query, {
    variables: { id: value as string },
    skip: !value,
  });

  const inputRef = createRef<HTMLInputElement>();

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      validity: { valid },
      files,
    } = event.target;

    const file = files?.[0];

    if (!file) return;

    if (maxSize && file.size > maxSize) {
      return enqueueSnackbar(`Размер файла превышает ${maxSize} байт`, {
        variant: "error",
      });
    }

    if (valid) {
      // @TODO: заменить any на правильный тип
      mutate({ variables: { input: { file: file as any } } });
    }
  };

  const onDragEnter = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDrag(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDrag(false);
  };

  const onDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const file = event.dataTransfer.files[0];

    if (file) {
      mutate({ variables: { input: { file: file as any } } });
    }

    setDrag(false);
  };

  const onClickDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onChange(null);
    mutationResult.reset();
    enqueueSnackbar(finalFileDeletedText, { variant: "success" });
  };

  const onClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (url) {
      window.open(url, "_blank");
    }
  };

  const loading = mutationResult.loading || queryResult.loading;
  const file = mutationResult.data?.file || queryResult.data?.file;
  const url = file?.url;

  return (
    <>
      <FormControl
        component="fieldset"
        fullWidth
        required={required}
        sx={{
          border: "1px solid",
          borderColor: theme.palette.divider,
          borderRadius: 1,
          padding: 2,
          position: "relative",
          "&:hover": {
            borderColor: theme.palette.text.secondary,
          },
        }}
      >
        {label && (
          <FormLabel
            component="legend"
            sx={{
              float: "unset",
              display: "block",
              width: "auto",
              padding: "0 5px",
              height: "11px",
              fontSize: "0.75em",
              top: -3,
              maxWidth: "100%",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </FormLabel>
        )}

        {!url ? (
          <ButtonBase
            sx={{
              width: "100%",
              minHeight: 120,
              backgroundColor: "rgba(0, 0, 0, 0.23)",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "3rem",
              color: drag ? "success.main" : "text.disabled",
              border: "2px dashed",
              borderColor: drag || loading ? "#2e7d32" : "rgba(0, 0, 0, 0.38)",
              gap: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              },
            }}
            onClick={onClick}
            onDragOverCapture={onDragOver}
            onDragEnterCapture={onDragEnter}
            onDragLeaveCapture={onDragLeave}
            onDropCapture={onDrop}
          >
            {loading ? (
              <CircularProgress
                sx={{ pointerEvents: "none" }}
                color="success"
              />
            ) : (
              <>
                <UploadIcon
                  fontSize="inherit"
                  color="inherit"
                  sx={{ pointerEvents: "none" }}
                />
                <Typography variant="body2" color="text.secondary">
                  Нажмите или перетащите файл для загрузки
                </Typography>
              </>
            )}
          </ButtonBase>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              borderRadius: 1,
            }}
          >
            <DescriptionIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Файл загружен
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Нажмите "Скачать" для загрузки файла
              </Typography>
            </Box>
          </Box>
        )}

        <input
          accept={accept}
          required={required && !value}
          type="file"
          style={{
            position: "absolute",
            opacity: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
          }}
          ref={inputRef}
          onChange={onChangeInput}
        />

        {value && (
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              onClick={onClickDownload}
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{ flex: 1 }}
            >
              {finalDownloadText}
            </Button>
            <Button onClick={onClickDelete} color="inherit">
              {finalDeleteFileText}
            </Button>
          </Box>
        )}
      </FormControl>
      {helperText && (
        <FormHelperText
          style={{
            marginTop: theme.spacing(0.5),
            marginRight: theme.spacing(2),
            marginBottom: 0,
            marginLeft: theme.spacing(2),
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </>
  );
}
